import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sparkles, Loader2 } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import OpportunityCard from "@/components/OpportunityCard";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import FeatureGate from "@/components/FeatureGate";

const EXAMPLES = [
  "Find warehouses over 100,000 square feet with tax delinquency near Seattle",
  "Show manufacturing businesses likely to close in California",
  "Foreclosures under $500,000 in Texas",
  "Restaurant closures in Florida with AI score above 75",
  "Vacant retail properties in New York",
];

export default function AISearchPage() {
  return (
    <FeatureGate feature="ai_search" title="AI Search is a premium feature" blurb="Natural language search across every distressed asset in the US. Included in Broker, Enterprise, and Institutional.">
      <AISearchInner />
    </FeatureGate>
  );
}

function AISearchInner() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("q") || "";
  const [q, setQ] = useState(initial);
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState(null);
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(false);

  const search = async (query) => {
    if (!query?.trim()) return;
    setLoading(true);
    setParams({ q: query });
    try {
      const [r1, r2] = await Promise.all([
        api.post("/ai/nl-search", { query }),
        api.post("/ai/ask", { query }),
      ]);
      setItems(r1.data.items || []);
      setFilters(r1.data.filters);
      setAnswer(r2.data.answer);
    } catch (e) {
      toast.error("AI search failed");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (initial) search(initial);
     
  }, []);

  return (
    <div className="space-y-6 max-w-6xl" data-testid="ai-search-page">
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Sparkles size={14} className="text-[#d4a574]" />
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#d4a574] font-semibold">AI-Powered Natural Language Search</div>
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Ask anything about US distressed assets.</h1>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); search(q); }} className="flex gap-2" data-testid="ai-search-form">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. warehouses over 100K sqft with tax delinquency near Seattle" data-testid="ai-search-input" className="bg-[#1c1712] border-[#33291f] h-11" />
        <button type="submit" disabled={loading} data-testid="ai-search-submit" className="px-5 rounded bg-[#d4a574] text-[#14100c] font-semibold hover:bg-[#e8b988] disabled:opacity-60 flex items-center gap-2">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
          Search
        </button>
      </form>

      {!items.length && !loading && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#7a6d5a] mb-2 font-semibold">Try an example</div>
          <div className="flex flex-wrap gap-2">
            {EXAMPLES.map((ex) => (
              <button key={ex} onClick={() => { setQ(ex); search(ex); }} data-testid={`ai-example-${ex.slice(0,10)}`} className="text-[12px] px-3 py-2 rounded border border-[#33291f] text-[#b8ac96] hover:border-[#d4a574] hover:text-[#d4a574] text-left">
                "{ex}"
              </button>
            ))}
          </div>
        </div>
      )}

      {answer && (
        <div className="glass rounded-lg p-5 border-l-2 border-[#d4a574]" data-testid="ai-answer">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#d4a574] mb-2 font-semibold flex items-center gap-2"><Sparkles size={11} /> AI Analyst</div>
          <div className="text-[14px] text-[#f4ede2] leading-relaxed">{answer}</div>
          <div className="text-[9px] text-[#7a6d5a] mt-3 uppercase tracking-wider font-mono">AI-Generated · Not investment advice</div>
        </div>
      )}

      {filters && Object.keys(filters).length > 0 && (
        <div className="text-[11px] font-mono text-[#7a6d5a]" data-testid="ai-filters">
          Interpreted as: {JSON.stringify(filters)}
        </div>
      )}

      {items.length > 0 && (
        <div>
          <div className="text-[12px] text-[#b8ac96] mb-3">{items.length} matches</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((o) => <OpportunityCard key={o.id} opp={o} />)}
          </div>
        </div>
      )}
    </div>
  );
}
